<section class="bg-star-gray-600 pt-[40px] pb-[60px] xl:pt-[64px] xl:pb-[100px]">
  <div class="container">
    <div class="max-w-[820px]">
      <h1 class="text-[29px] leading-[1.25] text-white md:text-[40px] xl:text-[44px]">
        Crypto Transaction Monitoring for AML Compliance
      </h1>
      <p class="mt-[24px] text-[16px] leading-[1.8] text-[#C0C0C0] md:text-[18px]">
        A wallet that was clean this morning can receive funds from a sanctioned counterparty this
        afternoon. AMLOfficer keeps wallets and their flows under continuous watch across four AML
        data providers, and raises an alert the moment the activity changes — in the dashboard and
        in your team's Telegram group.
      </p>
      <div class="mt-[32px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
        <a
          [href]="appLinks.createAccountHref()"
          target="_blank"
          class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
        >
          <span class="text-[16px] tracking-[0.3px]">Register Business Account</span>
          <mat-icon
            svgIcon="second-arrow-right"
            class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
          ></mat-icon>
        </a>
        <span class="text-[14px] leading-[1.6] text-white/50">
          Monitoring is set up in the AMLOfficer application after registration.
        </span>
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      A check is a moment. Monitoring is a habit.
    </h2>
    <div class="mt-[24px] grid gap-[20px] md:grid-cols-2">
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">A single AML check</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          You ask about one address, you get its risk picture, you make one decision. That is enough
          for a counterparty you deal with once — and it is what an
          <a routerLink="/aml-check" class="text-white underline underline-offset-4"
            >online AML check</a
          >
          is for.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Ongoing monitoring</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          The wallet stays under watch after the first answer. New deposits, new withdrawals and new
          risk connections are evaluated as they appear, and you hear about them without asking
          again.
        </p>
      </div>
    </div>
    <p class="mt-[24px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
      Most compliance work is the second kind. Counterparties you settle with weekly, merchant
      wallets, client deposit addresses and your own hot wallets all keep moving after the check
      that cleared them, and the risk that matters usually arrives later.
    </p>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container flex flex-col gap-[40px] lg:flex-row lg:gap-[60px]">
    <div class="flex-1">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        What continuous monitoring covers
      </h2>
      <ul class="mt-[16px] flex flex-col gap-[14px]">
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Wallets added manually, one at a time, or handed over to automated real-time monitoring.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            The scope you choose per wallet: deposits, withdrawals, or the full transaction history.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Risk signals from four AML data providers, so a flag one source misses can still reach
            you.
          </span>
        </li>
        <li class="flex items-start gap-[10px] text-[16px] leading-[1.8] text-[#C0C0C0]">
          <mat-icon
            svgIcon="check-circle-white-marker"
            class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
          ></mat-icon>
          <span>
            Alerts the moment suspicious activity is detected, so the team can act before the
            exposure grows.
          </span>
        </li>
      </ul>
    </div>
    <div class="flex-1">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        Alerts where your team already is
      </h2>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        An alert nobody opens is not a control. AMLOfficer delivers wallet compliance reports and
        instant alerts into Telegram, including group notifications, so a flagged deposit lands in
        the same chat where your compliance and operations people already talk to each other.
      </p>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        The dashboard keeps the full picture: monitored wallets, their reports and the history
        behind every alert, ready to be handed to an auditor or attached to a formal request without
        reconstructing it from screenshots.
      </p>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <div class="rounded-[10px] bg-[#25282D] px-[20px] py-[32px] lg:px-[40px] lg:py-[44px]">
      <h2 class="max-w-[820px] text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
        Monitoring is only as good as the data behind it
      </h2>
      <p class="mt-[16px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
        Every alert traces back to a dataset. AMLOfficer aggregates four AML data providers,
        including BitOK, Global Ledger and Beosin, and watches your wallets through all of them at
        once. Where a single-vendor tool gives you one opinion of an address, aggregation surfaces
        the cases where the sources disagree — which is when a compliance officer most wants to
        look.
      </p>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      Built for compliance teams, not for one analyst
    </h2>
    <div class="mt-[24px] grid gap-[20px] md:grid-cols-2 xl:grid-cols-3">
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Roles and shared access</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Invite your team, assign roles and distribute responsibilities securely, so monitoring
          does not depend on one person being online.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Shared investigations</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Reports and monitored wallets sit in a shared workspace, so a case picked up by a
          colleague starts from what has already been found.
        </p>
      </div>
      <div class="flex flex-col gap-[10px] rounded-[10px] bg-[#25282D] px-[20px] py-[24px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Answers to formal requests</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Wallet activity that has been monitored continuously is far easier to explain to a
          regulator or a law-enforcement request than a history reconstructed afterwards.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      Who keeps wallets under watch
    </h2>
    <div class="mt-[24px] grid gap-[20px] md:grid-cols-2">
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          VASPs and virtual asset platforms
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Client deposit addresses are watched for the whole life of the relationship, not only at
          onboarding, and every answer to a regulator can point at a documented timeline.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">Crypto exchangers</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Incoming assets are screened continuously, so an address that becomes linked to suspicious
          activity after your first check does not quietly stay on the whitelist.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">OTC desks</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Recurring counterparties are re-evaluated on their own activity rather than on a check
          that was run before the last three settlements.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Compliance and financial departments
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Merchant and counterparty wallets are centralised in one place, with alerts instead of
          scheduled manual reviews across scattered tools.
        </p>
      </div>
    </div>
    <p class="mt-[24px] max-w-[820px] text-[16px] leading-[1.8] text-[#C0C0C0]">
      Teams that want monitoring inside their own product connect it through the
      <a routerLink="/aml-api" class="text-white underline underline-offset-4">AMLOfficer AML API</a
      >, so wallets and risk signals appear in the systems your operations already run on.
    </p>
  </div>
</section>

<section class="bg-[#1C1E22] pb-[60px] lg:pb-[100px]">
  <div class="container">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px]">
      Transaction monitoring: common questions
    </h2>
    <div class="mt-[24px] grid gap-[24px] md:grid-cols-2">
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          What is KYT, or Know Your Transaction?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          KYT is the transaction-level counterpart of KYC: instead of asking who the customer is, it
          asks what their funds have been doing. Crypto transaction monitoring is how KYT is put
          into practice — the flows around a wallet are evaluated continuously rather than once.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          How is this different from an AML check?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          An AML check is a single question about an address at a single moment. Monitoring keeps
          that address under observation and tells you when the answer changes.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">What triggers an alert?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Activity on a monitored wallet that the AML data providers associate with risk. You choose
          the scope you care about per wallet — deposits, withdrawals or the full history — and the
          alert follows that scope.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Is transaction monitoring software enough for compliance?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          No tool makes an organisation compliant on its own. Monitoring gives your team the risk
          signals and the documented history; the policy, the thresholds and the decisions remain
          yours.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          How do we start monitoring a wallet?
        </h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Open a business account, add the wallet — manually or through the API — and choose the
          scope. Alerts then arrive in the dashboard and in Telegram.
        </p>
      </div>
      <div class="flex flex-col gap-[10px]">
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">What does monitoring cost?</h3>
        <p class="text-[16px] leading-[1.8] text-[#C0C0C0]">
          Terms depend on volume, so there is no single public price. AMLOfficer works on a
          pay-as-you-go model, and your plan is shown in your account after registration.
        </p>
      </div>
    </div>
  </div>
</section>

<app-business-account-cta
  heading="Put your wallets under continuous watch"
  text="Open a business account to set up monitoring, choose what each wallet is watched for and route alerts to the people who act on them."
></app-business-account-cta>

<app-related-pages [pages]="relatedPages"></app-related-pages>
